import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  GetReferenceDetailDocument,
  GetReferenceForFormDocument,
  GetReferencePublishersDocument,
  GetReferencesDocument,
  UpsertReferenceDocument,
  type UpsertReferenceMutation,
  type UpsertReferenceMutationVariables,
} from "~/features/references/gqls";
import type { ReferenceFormData } from "~/features/references/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/**
 * Create or update a reference via the single `cnutUpsertReference` mutation —
 * `id` present ⇒ update, absent ⇒ insert. Returns `{ submit, processing }`.
 */
export function useReferenceUpsert({
  onSuccess,
  initialPublisherIds,
  activePublisherIds,
}: {
  onSuccess?: () => void;
  initialPublisherIds?: string[];
  activePublisherIds?: string[];
} = {}) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [upsert] = useMutation<UpsertReferenceMutation, UpsertReferenceMutationVariables>(UpsertReferenceDocument);

  const submit = useCallback(
    async (form: ReferenceFormData) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      const isEdit = Boolean(form.id);
      const publishersUnchanged =
        isEdit &&
        initialPublisherIds &&
        form.newPublishers.length === 0 &&
        form.publisherIds.length === initialPublisherIds.length &&
        form.publisherIds.every((id) => initialPublisherIds.includes(id));
      if (
        !publishersUnchanged &&
        activePublisherIds &&
        form.publisherIds.some((id) => !activePublisherIds.includes(id))
      ) {
        toast.error(t("feature.references.form.removeInactivePublishers"));
        return false;
      }
      setProcessing(true);
      try {
        await upsert({
          refetchQueries: [
            { query: GetReferencePublishersDocument, variables: { tenantId } },
            { query: GetReferencesDocument, variables: { tenantId } },
            ...(isEdit && form.id
              ? [
                  { query: GetReferenceDetailDocument, variables: { tenantId, id: form.id } },
                  { query: GetReferenceForFormDocument, variables: { tenantId, id: form.id } },
                ]
              : []),
          ],
          variables: {
            input: {
              tenantId,
              ...(form.id ? { id: form.id } : {}),
              title: form.title,
              shortCode: form.shortCode,
              isbn: form.isbn,
              authors: form.authors.map((a) => a.name),
              documentUrl: form.documentUrl || null,
              edition: form.edition.trim() || null,
              publishedDate: form.publishedDate || null,
              publishers: publishersUnchanged
                ? undefined
                : [
                    ...form.publisherIds.map((publisherId) => ({ publisherId })),
                    ...form.newPublishers.map(({ name }) => ({ name })),
                  ],
            },
          },
        });
        toast.success(
          isEdit
            ? t("feature.common.updatedSuccessfully", { model: "reference" })
            : t("feature.common.createdSuccessfully", { model: "reference" }),
        );
        onSuccess?.();
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [upsert, tenantId, t, onSuccess, initialPublisherIds, activePublisherIds],
  );

  return { submit, processing };
}
